import { createAction, props } from '@ngrx/store';
import { LicenceSummary, NewSubscription, Subscription } from '../services/models/subscriptions.model';
import { AccountDetails } from './accounts.state';
import { AccountNote } from '../services/models/account-notes.model';
import { Sort } from '@angular/material/sort';
import { AccountTeachers, NewAccountTeacher } from '../services/models/account-teachers.model';
import { AccountStudentProfile } from '../services/models/account-students.model';
import { AccountSchoolExternalIdentifier } from '../services/models/account-details.model';
import { AccountSearchResults } from '../services/models/account-search.model';
import { UsageReport } from '../services/models/account-usage.model';
import { StudentGroups } from '../services/models/account-students.model';

export const accountSearchPageActions = {
  userSubmittedAccountSearch: createAction(
    '[Account Search Page] user Submitted Account Search',
    props<{ searchTerm: string; selectedProperty: string }>()
  ),
  userClickedAccount: createAction('[Account Search Page] User Clicked Account', props<{ accountId: number }>()),
  userWantsArchivedAccounts: createAction(
    '[Account Search Page] User Wants Archived Accounts',
    props<{ showAnonymizedAccounts: boolean }>()
  ),
  userClickedLoadMoreAccounts: createAction(
    '[Account Search Page] User Clicked Load More Accounts',
    props<{ searchTerm: string; selectedProperty: string }>()
  ),
  userResetAccountSearch: createAction('[Account Search Page] User Reset Account Search')
};

export const accountSearchAPIActions = {
  searchAccountsSuccess: createAction(
    '[Account Search API] Search Accounts Success',
    props<{ searchResults: AccountSearchResults }>()
  ),
  searchAccountsFailure: createAction('[Account Search API] Search Accounts Failure', props<{ error: string }>())
};

export const accountPageActions = {
  initAccountPage: createAction('[Account Page] Init Account Page', props<{ accountId: number }>()),
  userAddedSubscription: createAction(
    '[Account Page] User Added Subscription',
    props<{ subscription: NewSubscription }>()
  ),
  userAddedSiteLicence: createAction(
    '[Account Page] User Added Site Licence',
    props<{ subscription: NewSubscription }>()
  ),
  userAddedLiteracyOffer: createAction(
    '[Account Page] User Added Literacy Offer',
    props<{ subscription: NewSubscription }>()
  ),
  userAddedFreeTrial: createAction('[Account Page] User Added Free Trial', props<{ subscription: NewSubscription }>()),
  userEditedSubscription: createAction(
    '[Account Page] User Edited Subscription',
    props<{ subscription: NewSubscription }>()
  ),
  userUpdatedSubscriptionFilter: createAction('[Account Page] Update Subscription Filter', props<{ filter: string }>()),
  userUpdatedSubscriptionTableSort: createAction(
    '[Account Page] Update Subscription Table Sort',
    props<{ sort: Sort }>()
  ),
  userSubmittedNote: createAction('[Account Page] User Submitted Note', props<{ note: string }>()),
  userUpdatedNotesFilter: createAction('[Account Page] Update Notes Filter', props<{ filter: string }>()),
  userEditedNote: createAction('[Account Page] User Editing Note', props<{ noteId: number; newNote: string }>()),
  userClickedExportAccount: createAction('[Account Page] User Clicked Export Account'),
  userClickedResendWelcomeEmail: createAction(
    '[Account Page] User Clicked Resend Welcome Email',
    props<{ isUDEmail: boolean }>()
  ),
  userClickedResendHubSpotIntegrationMessage: createAction(
    '[Account Page] User Clicked Resend HubSpot Integration Message'
  ),
  userUpdatedAccountDetails: createAction(
    '[Account Page] User Updated Account Details',
    props<{ changes: Partial<AccountDetails> }>()
  ),
  userChangedAccountLanguage: createAction('[Account Page] User Changed Account Language'),
  updateAccountStatus: createAction('[Account Page] User Updated Account Status', props<{ disableAccount: boolean }>()),
  activateAccount: createAction('[Account Page] User Activated Account'),
  deleteAccount: createAction('[Account Page] User Deleted Account'),
  userResetAccountPassword: createAction(
    '[Account Page] User Reset Account Password',
    props<{ userId: string; isUdTrainee: boolean; isAdmin: boolean }>()
  ),
  userUpdatedSchoolIdentifier: createAction(
    '[Account Teachers Page] User Updated School Identifier',
    props<{ changes: AccountSchoolExternalIdentifier }>()
  )
};

export const accountPageAPIActions = {
  loadAccountSuccess: createAction('[Account API] Load Account Success', props<{ account: AccountDetails }>()),
  loadAccountFailure: createAction('[Account API] Load Account Failure', props<{ error: string }>()),
  loadAccountSubscriptionSuccess: createAction(
    '[Account API] Load Account Subscription Success',
    props<{ subscriptions: Subscription[]; licenceSummaries: LicenceSummary[] }>()
  ),
  loadAccountSubscriptionFailure: createAction(
    '[Account API] Load Account Subscription Failure',
    props<{ error: string }>()
  ),
  addSubscriptionSuccess: createAction('[Account API] Add Subscription Success'),
  addSubscriptionFailure: createAction('[Account API] Add Subscription Failure', props<{ error: string }>()),
  editSubscriptionSuccess: createAction('[Account API] Add Subscription Success'),
  editSubscriptionFailure: createAction('[Account API] Add Subscription Failure', props<{ error: string }>()),
  addSiteLicenceSuccess: createAction('[Account API] Add Site Licence Success'),
  addSiteLicenceFailure: createAction('[Account API] Add Site Licence Failure', props<{ error: string }>()),
  addFreeTrialSuccess: createAction('[Account API] Add Free Trial Success'),
  addFreeTrialFailure: createAction('[Account API] Add Free Trial Failure', props<{ error: string }>()),
  addLiteracyOfferSuccess: createAction('[Account API] Add Literacy Offer Success'),
  addLiteracyOfferFailure: createAction('[Account API] Add Literacy Offer Failure', props<{ error: string }>()),
  loadAccountNotesSuccess: createAction('[Account API] Load Account Notes Success', props<{ notes: AccountNote[] }>()),
  loadAccountNotesFailure: createAction('[Account API] Load Account Notes Failure', props<{ error: string }>()),
  updateAccountNoteSuccess: createAction('[Account API] Update Account Note Success'),
  updateAccountNoteFailure: createAction('[Account API] Update Account Note Failure', props<{ error: string }>()),
  addAccountNoteSuccess: createAction('[Account API] Add Account Notes Success'),
  addAccountNoteFailure: createAction('[Account API] Add Account Notes Failure', props<{ error: string }>()),
  exportAccountSuccess: createAction('[Account API] Export Account Success'),
  exportAccountFailure: createAction('[Account API] Export Account Failure', props<{ error: string }>()),
  updateAccountDetailsSuccess: createAction('[Account API] Update Account Details Success'),
  updateAccountDetailsFailure: createAction('[Account API] Update Account Details Failure', props<{ error: string }>()),
  updateAccountDetailsPartialSuccess: createAction(
    '[Account API] Update Account Details Partial Success',
    props<{ error: string }>()
  ),
  resendWelcomeEmailSuccess: createAction('[Account API] Resend Welcome Email Success'),
  resendWelcomeEmailFailure: createAction('[Account API] Resend Welcome Email Failure', props<{ error: string }>()),
  resendHubSpotIntegrationMessageSuccess: createAction('[Account API] Resend HubSpot Integration Message Success'),
  resendHubSpotIntegrationMessageFailure: createAction(
    '[Account API] Resend HubSpot Integration Message Failure',
    props<{ error: string }>()
  ),
  changeAccountLanguageSuccess: createAction('[Account API] Change Account Language Success'),
  changeAccountLanguageFailure: createAction(
    '[Account API] Change Account Language Failure',
    props<{ error: string }>()
  ),
  deleteAccountSuccess: createAction('[Account API] Delete Account Success'),
  deleteAccountFailure: createAction('[Account API] Delete Account Failure', props<{ error: string }>()),
  activateAccountSuccess: createAction('[Account API] Activate Account Success'),
  activateAccountFailure: createAction('[Account API] Activate Account Failure', props<{ error: string }>()),
  changeStatusAccountSuccess: createAction('[Account API] Update Account Status Success'),
  changeStatusAccountFailure: createAction('[Account API] Update Account Status Failure', props<{ error: string }>()),
  resetAccountPasswordSuccess: createAction('[Account API] Reset Account Password Success'),
  resetAccountPasswordFailure: createAction('[Account API] Reset Account Password Failure', props<{ error: string }>()),
  updateExternalSchoolIdentifierSuccess: createAction('[Account API] Update External School Identifier Success'),
  updateExternalSchoolIdentifierFailure: createAction(
    '[Account API] Update External School Identifier Failure',
    props<{ error: string }>()
  )
};

export const teacherPageActions = {
  initTeacherPage: createAction('[Account Teachers Page] Init Teacher Page', props<{ accountId: number }>()),
  userSearchedTeachers: createAction('[Account Teachers Page] User Searched Teachers', props<{ searchTerm: string }>()),
  userUpdatedTeacherTableSort: createAction(
    '[Account Teachers Page] User Updated Teacher Table Sort',
    props<{ sort: Sort }>()
  ),
  userSelectedTeacherRole: createAction(
    '[Account Teachers Page] User Selected Teacher Role',
    props<{ roleType: string[] }>()
  ),
  userAddedTeacher: createAction('[Account Teachers Page] User Added Teacher', props<{ teacher: NewAccountTeacher }>()),
  userEditedTeacher: createAction(
    '[Account Teachers Page] User Edited Teacher',
    props<{ teacher: NewAccountTeacher }>()
  ),
  userDeletedTeacher: createAction('[Account Teachers Page] User Deleted Teacher', props<{ userId: string }>()),
  userResentTeacherWelcomeEmail: createAction(
    '[Account Teachers Page] User Resent Teacher Welcome Email',
    props<{ userId: string; isUdTrainee: boolean }>()
  ),
  userResetTeacherAccountPassword: createAction(
    '[Account Teachers Page] User Reset Teacher Account Password',
    props<{ userId: string; isUdTrainee: boolean; isAdmin: boolean }>()
  )
};

export const teacherApiActions = {
  loadAccountTeachersSuccess: createAction(
    '[Account Teachers API] Load Account Teachers Success',
    props<{ teachers: AccountTeachers }>()
  ),
  loadAccountTeachersFailure: createAction(
    '[Account Teachers API] Load Account Teachers Failure',
    props<{ error: string }>()
  ),
  searchAccountTeachersSuccess: createAction(
    '[Account Teachers API] Search Account Teachers Success',
    props<{ teachers: AccountTeachers }>()
  ),
  searchAccountTeachersFailure: createAction(
    '[Account Teachers API] Search Account Teachers Failure',
    props<{ error: string }>()
  ),
  exportAccountSuccess: createAction('[Account API] Export Account Success'),
  exportAccountFailure: createAction('[Account API] Export Account Failure', props<{ error: string }>()),
  editOrAddTeacherSuccess: createAction('[Account Teachers API] Add / Edit Account Teacher Success'),
  editOrAddTeacherFailure: createAction(
    '[Account Teachers API] Add / Edit Account Teacher Failure',
    props<{ error: string }>()
  ),
  deleteTeacherSuccess: createAction('[Account Teachers API] Delete Account Teacher Success'),
  deleteTeacherFailure: createAction(
    '[Account Teachers API] Delete Account Teacher Failure',
    props<{ error: string }>()
  ),
  resendTeacherWelcomeEmailSuccess: createAction('[Account Teachers API] Resend Teacher Welcome Email Success'),
  resendTeacherWelcomeEmailFailure: createAction(
    '[Account Teachers API] Resend Teacher Welcome Email Failure',
    props<{ error: string }>()
  )
};

export const studentsPageActions = {
  initStudentsPage: createAction(
    '[Account Students Page] Init Students Page',
    props<{ accountId: number; pageSize: number; searchTerm: string; group: string }>()
  ),
  sortStudents: createAction('[Account Students Page] Sort Students', props<{ sort: Sort }>()),
  userSearchedStudents: createAction(
    '[Account Students Page] User Searched Students',
    props<{ pageSize: number; searchTerm: string; group: string }>()
  ),
  userSelectedStudentGroup: createAction(
    '[Account Students Page] User Selected Student Group',
    props<{ pageSize: number; searchTerm: string; group: string }>()
  ),
  loadMoreStudents: createAction(
    '[Account Students Page] Load More Students',
    props<{ pageSize: number; searchTerm: string; group: string }>()
  ),
  userSyncedStudentLicenses: createAction(
    '[Account Students Page] User Synced Student Licenses',
    props<{ studentId: number }>()
  ),
  userSyncedAllAccountsStudentLicenses: createAction(
    '[Account Students Page] User Synced All Accounts Student Licenses'
  )
};

export const studentsPageAPIActions = {
  loadAccountStudentsSuccess: createAction(
    '[Account Students API] Load Account Students Success',
    props<{ students: AccountStudentProfile }>()
  ),
  loadAccountStudentsFailure: createAction(
    '[Account Students API] Load Account Students Failure',
    props<{ error: string }>()
  ),
  syncStudentLicensesSuccess: createAction(
    '[Account Students API] Sync Student Licenses Success',
    props<{ pageSize: number; searchTerm: string; group: string }>()
  ),
  syncStudentLicensesFailure: createAction(
    '[Account Students API] Sync Student Licenses Failure',
    props<{ error: string }>()
  ),
  syncAllAccountsStudentLicensesSuccess: createAction(
    '[Account Students API] Sync All Accounts Student Licenses Success',
    props<{ pageSize: number; searchTerm: string; group: string }>()
  ),
  syncAllAccountsStudentLicensesFailure: createAction(
    '[Account Students API] Sync All Accounts Student Licenses Failure',
    props<{ error: string }>()
  ),
  loadStudentGroupsSuccess: createAction(
    '[Account Students API] Load Student Groups Success',
    props<{ studentGroups: StudentGroups[] }>()
  ),
  loadStudentGroupsFailure: createAction(
    '[Account Students API] Load Student Groups Failure',
    props<{ error: string }>()
  )
};

export const accountUsagePageActions = {
  initAccountUsagePage: createAction('[Account Usage Page] Init Account Usage Page', props<{ accountId: number }>()),
  applyDateFilter: createAction(
    '[Account Usage Page] Apply Date Filter',
    props<{ startDate: string; endDate: string }>()
  ),
  resetDateFilter: createAction('[Account Usage Page] Reset Date Filter')
};

export const accountUsagePageAPIActions = {
  loadAccountUsageSuccess: createAction(
    '[Account Usage API] Load Account Usage Success',
    props<{ usageData: UsageReport }>()
  ),
  loadAccountUsageFailure: createAction('[Account Usage API] Load Account Usage Failure', props<{ error: string }>())
};
